import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, inject, Injector, OnDestroy, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AvatarSizeEnum, AvatarVariantEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/avatar';
import { AvatarNameModule } from '@vueling-ng/vy-ds/src/lib/components/globals/avatar-name';
import { BoxLevelEnum, BoxModule } from '@vueling-ng/vy-ds/src/lib/components/globals/box';
import { ButtonModule, ButtonSizeEnum, ButtonTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/button';
import { Chip, ChipModule } from '@vueling-ng/vy-ds/src/lib/components/globals/chip';
import { LoaderComponent, LoaderModule } from '@vueling-ng/vy-ds/src/lib/components/globals/loaders/loader';
import { MenuHorizontalComponent } from '@vueling-ng/vy-ds/src/lib/components/globals/menu-horizontal';
import { PaginatorModule } from '@vueling-ng/vy-ds/src/lib/components/globals/paginator';
import {
  SegmentedControlsComponent,
  SegmentedControlsModel
} from '@vueling-ng/vy-ds/src/lib/components/globals/segmented-controls';
import { SortEvent } from '@vueling-ng/vy-ds/src/lib/components/globals/table';
import { AccountManagerResourcesService } from 'app/services/api-services/accountManager/accountManager.resources';
import { catchError, EMPTY, Subject, take } from 'rxjs';
import { BookingTableComponent } from './components/booking-table/booking-table.component';
import { ExportBookingListComponent } from './components/export-booking-list/export-booking-list.component';
import { cancelledHeaders, defaultHeaders, headerToSortMap, TableHeaderId } from './models/booking-table-header.model';
import { EmptyImg } from './models/booking-type';
import { BookingsParams, SortColumn, SortCriteria } from './models/bookings.interface';
import { quickFilters, QuickFilters, QuickFiltersValue } from './models/filters.model';
import { BookingListFilterService } from './services/booking-list-filter.service';
import { BookingListService } from './services/booking-list.service';

@Component({
  selector: 'app-booking-list',
  templateUrl: './booking-list.component.html',
  styleUrl: './booking-list.component.scss',
  standalone: true,
  imports: [
    MenuHorizontalComponent,
    AvatarNameModule,
    TranslateModule,
    BoxModule,
    SegmentedControlsComponent,
    ButtonModule,
    ChipModule,
    ExportBookingListComponent,
    LoaderModule,
    BookingTableComponent,
    PaginatorModule,
    CommonModule,
    LoaderModule
  ]
})
export class BookingListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(LoaderComponent) private readonly loader!: LoaderComponent;

  private readonly _bookingListService = inject(BookingListService);
  private readonly _bookingListFilterService = inject(BookingListFilterService);
  private readonly _accountManagerService = inject(AccountManagerResourcesService);
  private readonly _injector = inject(Injector);

  public readonly bookingList = this._bookingListService.currentViewModelBooking();

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  public quickFilters: SegmentedControlsModel[] = quickFilters;
  public currentChips: Chip[] = [];
  public currentFilters = this._bookingListFilterService.getCurrentFilters();

  public bookingsNotFoundImage = EmptyImg;

  public readonly AvatarVariant = AvatarVariantEnum;
  public readonly AvatarSize = AvatarSizeEnum;
  public readonly ButtonType = ButtonTypeEnum;
  public readonly ButtonSize = ButtonSizeEnum;
  public readonly BoxLevel = BoxLevelEnum;

  public ngAfterViewInit(): void {
    this._accountManagerService.createCookie().subscribe();
    this._bookingListService.setTableHeaders(defaultHeaders);
    this.startLoading();
    this._bookingListFilterService.loadFilters().subscribe();

    effect(
      () => {
        const bookingsParams: BookingsParams = {
          pageNumber: 1,
          filters: this.currentFilters()
        };
        this.loadBookings(bookingsParams);
        this.currentChips = this._bookingListFilterService.getChipsFromFilters(this.currentFilters());
      },
      { injector: this._injector }
    );
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public changeQuickFilter(segmentedControl: SegmentedControlsModel): void {
    if (!this.loader) {
      return;
    }
    this.startLoading();

    if (segmentedControl.value === QuickFiltersValue.CancelledQuickFilters) {
      this._bookingListService.setTableHeaders(cancelledHeaders);
    } else {
      this._bookingListService.setTableHeaders(defaultHeaders);
    }

    this._bookingListFilterService.changeQuickFilter(segmentedControl.value as QuickFilters);

    this._bookingListService.handleAnalyticsEvent(segmentedControl);
  }

  public changePage(pageNumber: number): void {
    this.startLoading();

    const bookingParams: BookingsParams = {
      pageNumber: pageNumber
    };

    this.loadBookings(bookingParams);
  }

  public openFiltersSidePanel(): void {
    this._bookingListFilterService.openFiltersSidePanel();
  }

  public deleteFilter(filterId: string): void {
    this._bookingListFilterService.deleteFilter(filterId);
  }

  public onSort(event: SortEvent): void {
    const sortCriteria: SortCriteria = {
      column: this.getSortColumn(event.column as TableHeaderId),
      direction: event.direction
    };

    const bookingsParams: BookingsParams = {
      pageNumber: 1,
      sortCriteria: sortCriteria
    };

    this.loadBookings(bookingsParams);
  }

  public startLoading(): void {
    this.loader.loadingProcess();
  }

  public stopLoading(): void {
    this.loader.loadingDone();
  }

  private loadBookings(bookingsParams: BookingsParams): void {
    this.loader.loadingProcess();

    this._bookingListService
      .updateBookings(bookingsParams)
      .pipe(
        take(1),
        catchError(() => {
          this.stopLoading();
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.stopLoading();
      });
  }

  private getSortColumn(header: TableHeaderId): SortColumn {
    return headerToSortMap[header] ?? 'None';
  }
}
